import React, { useCallback, useEffect, useRef, useState } from "react";

const ARCHITECTURE_CARDS = [
  {
    title: "Audio Input & Playback",
    items: [
      "Upload an audio file or paste a direct URL to queue new material.",
      "A hidden HTML5 <audio> element does the heavy lifting for decoding and playback.",
      "Play/pause, volume, and status indicators keep you in control."
    ]
  },
  {
    title: "Audio Analysis",
    items: [
      "Web Audio API's AudioContext and AnalyserNode extract real-time spectrum data.",
      "createMediaElementSource() feeds the analyzer while still routing to the destination.",
      "getByteFrequencyData() produces the values that drive the visuals."
    ]
  },
  {
    title: "Visual Rendering",
    items: [
      "An HTML5 canvas is resized to the layout and redrawn via requestAnimationFrame().",
      "Bars, radial pointers, and particle dots react directly to the amplitude for each frame.",
      "A gradient background and soft gradients keep the focus on the beats."
    ]
  }
];

const VISUAL_MODES = [
  { key: "bars", label: "Spectrum Bars" },
  { key: "radial", label: "Radial Pointers" },
  { key: "particles", label: "Particle Field" }
];

function MusicVisualizerPage() {
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationFrameRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const lastObjectUrlRef = useRef(null);
  const modeRef = useRef("bars");

  const [audioSource, setAudioSource] = useState("");
  const [inputUrl, setInputUrl] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.72);
  const [visualMode, setVisualMode] = useState("bars");
  const [status, setStatus] = useState("Upload a track or paste a URL to begin.");
  const [sourceLabel, setSourceLabel] = useState("No track selected");

  useEffect(() => {
    modeRef.current = visualMode;
  }, [visualMode]);

  const resizeCanvas = useCallback(() => {
    if (!canvasRef.current || !wrapperRef.current) return;
    const width = Math.max(wrapperRef.current.clientWidth, 520);
    const height = Math.max(Math.min(wrapperRef.current.clientHeight, 420), 320);
    canvasRef.current.width = width;
    canvasRef.current.height = height;
  }, []);

  const resetAudioEngine = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.disconnect();
      } catch (error) {
        console.warn("source disconnect error", error);
      }
      sourceNodeRef.current = null;
    }

    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (error) {
        console.warn("analyser disconnect error", error);
      }
      analyserRef.current = null;
    }

    dataArrayRef.current = null;

    if (audioContextRef.current) {
      audioContextRef.current
        .close()
        .catch(() => {})
        .finally(() => {
          audioContextRef.current = null;
        });
    }
  }, []);

  const startVisualizerLoop = useCallback(() => {
    if (!canvasRef.current || !analyserRef.current || !dataArrayRef.current) {
      return;
    }

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const drawFrame = () => {
      if (!canvasRef.current || !analyserRef.current || !dataArrayRef.current) {
        animationFrameRef.current = requestAnimationFrame(drawFrame);
        return;
      }

      const width = canvasRef.current.width;
      const height = canvasRef.current.height;
      const dataArray = dataArrayRef.current;
      analyserRef.current.getByteFrequencyData(dataArray);

      ctx.clearRect(0, 0, width, height);
      const bgGradient = ctx.createLinearGradient(0, 0, width, height);
      bgGradient.addColorStop(0, "rgba(6,8,24,0.7)");
      bgGradient.addColorStop(1, "rgba(14,10,30,0.95)");
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);

      const mode = modeRef.current;
      const centerX = width / 2;
      const centerY = height / 2;
      ctx.lineCap = "round";

      if (mode === "radial") {
        const radius = Math.min(width, height) / 3;
        const step = (Math.PI * 2) / (dataArray.length || 60);
        const hueStep = 360 / Math.max(dataArray.length, 60);
        for (let i = 0; i < dataArray.length; i += 4) {
          const value = dataArray[i] / 255;
          const angle = i * step;
          const outerRadius = radius + value * (radius * 0.9);
          const targetX = centerX + Math.cos(angle) * outerRadius;
          const targetY = centerY + Math.sin(angle) * outerRadius;
          ctx.strokeStyle = `hsla(${Math.round(i * hueStep)}, 95%, 70%, ${0.7 - value * 0.4})`;
          ctx.lineWidth = 1 + value * 6;
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.lineTo(targetX, targetY);
          ctx.stroke();
        }
        ctx.beginPath();
        ctx.arc(centerX, centerY, Math.min(width, height) / 8, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.15)";
        ctx.fill();
      } else if (mode === "particles") {
        const particleCount = 48;
        for (let i = 0; i < particleCount; i += 1) {
          const index = Math.floor((i / particleCount) * dataArray.length);
          const value = (dataArray[index] ?? 0) / 255;
          const angle = (i / particleCount) * Math.PI * 2;
          const spread = Math.min(width, height) / 4 + value * (Math.min(width, height) / 3);
          const dotX = centerX + Math.cos(angle) * spread;
          const dotY = centerY + Math.sin(angle) * spread;
          ctx.fillStyle = `rgba(255,255,255,${0.65 - value * 0.45})`;
          ctx.beginPath();
          ctx.arc(dotX, dotY, 6 + value * 8, 0, Math.PI * 2);
          ctx.fill();
        }
      } else {
        const barWidth = Math.max(width / (dataArray.length || 64), 4);
        const barGradient = ctx.createLinearGradient(0, 0, width, height);
        barGradient.addColorStop(0, "rgba(184,111,255,0.95)");
        barGradient.addColorStop(0.5, "rgba(129,212,250,0.7)");
        barGradient.addColorStop(1, "rgba(72,238,255,0.55)");
        ctx.fillStyle = barGradient;
        dataArray.forEach((value, index) => {
          const percent = value / 255;
          const barHeight = percent * height * 0.65;
          const x = index * barWidth;
          ctx.fillRect(x, height - barHeight, barWidth - 1, barHeight);
        });
      }

      animationFrameRef.current = requestAnimationFrame(drawFrame);
    };

    drawFrame();
  }, []);

  const initializeAudioAnalyzer = useCallback(async () => {
    if (!audioRef.current || !audioSource) {
      return;
    }

    resetAudioEngine();

    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      setStatus("AudioContext is unavailable in this browser.");
      return;
    }

    const context = new AudioContext();
    audioContextRef.current = context;
    try {
      await context.resume();
    } catch (error) {
      console.warn("audio context resume", error);
    }

    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.85;
    analyserRef.current = analyser;

    const sourceNode = context.createMediaElementSource(audioRef.current);
    sourceNodeRef.current = sourceNode;
    sourceNode.connect(analyser);
    analyser.connect(context.destination);

    dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

    startVisualizerLoop();
    setStatus("Audio is ready. Play to see the spectrum.");
  }, [audioSource, resetAudioEngine, startVisualizerLoop]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    return () => {
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
    };
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;
    if (!audioSource) {
      audioRef.current.removeAttribute("src");
      audioRef.current.load();
      setIsPlaying(false);
      return;
    }
    audioRef.current.load();
    setStatus("Loading audio...");
    setIsPlaying(false);
  }, [audioSource]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const handleCanPlay = () => initializeAudioAnalyzer();
    audio.addEventListener("canplay", handleCanPlay);
    return () => {
      audio.removeEventListener("canplay", handleCanPlay);
    };
  }, [audioSource, initializeAudioAnalyzer]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [resizeCanvas]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    return () => {
      resetAudioEngine();
      if (lastObjectUrlRef.current) {
        URL.revokeObjectURL(lastObjectUrlRef.current);
      }
    };
  }, [resetAudioEngine]);

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (lastObjectUrlRef.current) {
      URL.revokeObjectURL(lastObjectUrlRef.current);
    }
    const tempUrl = URL.createObjectURL(file);
    lastObjectUrlRef.current = tempUrl;
    setSourceLabel(file.name);
    setAudioSource(tempUrl);
    setStatus(`Using ${file.name}`);
  };

  const handleUrlSubmit = () => {
    const trimmed = inputUrl.trim();
    if (!trimmed) {
      setStatus("Please paste a valid URL before loading.");
      return;
    }
    setSourceLabel(trimmed);
    setAudioSource(trimmed);
    setInputUrl("");
    setStatus("Fetching remote audio...");
  };

  const handleClear = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (lastObjectUrlRef.current) {
      URL.revokeObjectURL(lastObjectUrlRef.current);
      lastObjectUrlRef.current = null;
    }
    setAudioSource("");
    setSourceLabel("No track selected");
    setStatus("Audio cleared. Upload another source.");
    setIsPlaying(false);
    resetAudioEngine();
  };

  const handleTogglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio || !audioSource) {
      setStatus("Load audio before hitting play.");
      return;
    }

    if (audio.paused) {
      try {
        await audioContextRef.current?.resume();
      } catch (error) {
        console.warn("resume error", error);
      }
      try {
        await audio.play();
      } catch (error) {
        setStatus("Playback is blocked. Click play one more time.");
        console.warn("play error", error);
      }
    } else {
      audio.pause();
    }
  };

  return (
    <div style={styles.page}>
      <section style={styles.hero}>
        <div>
          <h1 style={styles.heading}>Music Visualizer</h1>
          <p style={styles.subheading}>
            Pipe audio into a Web Audio analyzer, stream frequency data, and render immersive visuals inside
            an HTML5 canvas.
          </p>
        </div>
        <div style={styles.cardGrid}>
          {ARCHITECTURE_CARDS.map((card) => (
            <article key={card.title} style={styles.card}>
              <h3 style={styles.cardTitle}>{card.title}</h3>
              <ul style={styles.cardList}>
                {card.items.map((item) => (
                  <li key={item} style={styles.cardItem}>
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section style={styles.controls}>
        <div style={styles.row}>
          <label style={styles.uploadLabel}>
            <span style={styles.uploadText}>Upload a file</span>
            <input type="file" accept="audio/*" onChange={handleFileUpload} style={styles.fileInput} />
          </label>
          <div style={styles.urlInputContainer}>
            <input
              type="url"
              value={inputUrl}
              onChange={(event) => setInputUrl(event.target.value)}
              placeholder="https://example.com/track.mp3"
              style={styles.urlInput}
            />
            <button type="button" onClick={handleUrlSubmit} style={styles.loadUrlButton}>
              Load URL
            </button>
          </div>
          <button type="button" onClick={handleClear} style={styles.clearButton}>
            Clear
          </button>
        </div>
        <div style={styles.playbackRow}>
          <button type="button" onClick={handleTogglePlayback} style={styles.playButton}>
            {isPlaying ? "Pause" : "Play"}
          </button>
          <div style={styles.volumeControl}>
            <label htmlFor="volume">Volume</label>
            <input
              id="volume"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(event) => setVolume(Number(event.target.value))}
              style={styles.volumeSlider}
            />
          </div>
          <div style={styles.sourceBadge}>
            Source: <span style={styles.sourceLabel}>{sourceLabel}</span>
          </div>
        </div>
        <div style={styles.modeRow}>
          {VISUAL_MODES.map((mode) => (
            <button
              key={mode.key}
              type="button"
              onClick={() => setVisualMode(mode.key)}
              style={{
                ...styles.modeButton,
                ...(visualMode === mode.key ? styles.modeButtonActive : {})
              }}
            >
              {mode.label}
            </button>
          ))}
        </div>
      </section>

      <section style={styles.visualSection} ref={wrapperRef}>
        <canvas ref={canvasRef} style={styles.canvas} />
        <div style={styles.statusRow}>{status}</div>
        <audio ref={audioRef} src={audioSource || undefined} crossOrigin="anonymous" hidden />
      </section>
    </div>
  );
}

const styles = {
  page: {
    color: "#f4f5ff",
    display: "flex",
    flexDirection: "column",
    gap: 24,
    padding: "24px 20px 40px",
    maxWidth: 1200,
    margin: "0 auto"
  },
  hero: {
    background: "rgba(12, 10, 36, 0.75)",
    padding: 24,
    borderRadius: 20,
    border: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 16
  },
  heading: {
    margin: 0,
    fontSize: 32,
    letterSpacing: 0.6
  },
  subheading: {
    margin: 0,
    fontSize: 16,
    color: "rgba(255,255,255,0.75)"
  },
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12
  },
  card: {
    padding: 16,
    background: "rgba(255,255,255,0.04)",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.08)",
    minHeight: 160
  },
  cardTitle: {
    margin: "0 0 8px",
    fontSize: 16
  },
  cardList: {
    margin: 0,
    paddingLeft: 16,
    fontSize: 14,
    color: "rgba(255,255,255,0.85)"
  },
  cardItem: {
    marginBottom: 6
  },
  controls: {
    background: "rgba(10, 10, 32, 0.85)",
    borderRadius: 20,
    padding: 20,
    border: "1px solid rgba(255,255,255,0.08)",
    display: "flex",
    flexDirection: "column",
    gap: 12
  },
  row: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center"
  },
  uploadLabel: {
    display: "inline-flex",
    flexDirection: "column",
    gap: 8,
    cursor: "pointer",
    fontSize: 12,
    letterSpacing: 0.5
  },
  uploadText: {
    fontSize: 12,
    color: "rgba(255,255,255,0.7)"
  },
  fileInput: {
    cursor: "pointer",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.2)",
    padding: "6px 10px",
    background: "rgba(255,255,255,0.05)"
  },
  urlInputContainer: {
    display: "flex",
    flex: 1,
    gap: 8,
    minWidth: 180
  },
  urlInput: {
    flex: 1,
    borderRadius: 12,
    padding: "10px 14px",
    border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(255,255,255,0.05)",
    color: "white"
  },
  loadUrlButton: {
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,0.35)",
    background: "linear-gradient(120deg, rgba(184,111,255,0.3), rgba(129,212,250,0.25))",
    color: "white",
    cursor: "pointer",
    padding: "10px 16px"
  },
  clearButton: {
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.35)",
    background: "transparent",
    color: "white",
    cursor: "pointer",
    padding: "8px 20px"
  },
  playbackRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap"
  },
  playButton: {
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(120deg, #b66cff, #4fc3f7)",
    color: "#09080f",
    padding: "10px 22px",
    fontWeight: 600,
    cursor: "pointer"
  },
  volumeControl: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "rgba(255,255,255,0.75)"
  },
  volumeSlider: {
    cursor: "pointer"
  },
  sourceBadge: {
    marginLeft: "auto",
    fontSize: 12,
    color: "rgba(255,255,255,0.6)"
  },
  sourceLabel: {
    fontWeight: 600,
    color: "rgba(255,255,255,0.9)"
  },
  modeRow: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap"
  },
  modeButton: {
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: 12,
    padding: "8px 16px",
    background: "transparent",
    color: "white",
    cursor: "pointer"
  },
  modeButtonActive: {
    background: "rgba(184,111,255,0.25)",
    borderColor: "rgba(184,111,255,0.7)"
  },
  visualSection: {
    position: "relative",
    background: "rgba(255,255,255,0.02)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 20,
    padding: 20,
    minHeight: 360,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 12
  },
  canvas: {
    borderRadius: 16,
    width: "100%",
    height: 360,
    background: "#050910"
  },
  statusRow: {
    fontSize: 13,
    color: "rgba(255,255,255,0.7)"
  }
};

export default MusicVisualizerPage;
